import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { leadsApi, usersApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { PlusIcon, TrashIcon, EditIcon } from '../../utils/icons';
import { formatDate, timeAgo } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './LeadDetail.css';

const STAGES = ['New', 'Contacted', 'Site Visit', 'Negotiation', 'Closed', 'Lost'];
const SOURCES = ['99acres', 'MagicBricks', 'Housing.com', 'Manual'];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [lead, setLead] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      setError('');
      const res = await leadsApi.get(id);
      setLead(res.data);
    } catch (err) {
      if (err.response?.status === 404) setError('Lead not found.');
      else if (err.response?.status === 403) setError('You do not have access to this lead.');
      else setError('Failed to load lead.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await leadsApi.activity(id);
      setActivity(res.data);
    } catch {
      // Non-critical
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch {
      // Non-critical
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchLead(); fetchActivity(); fetchUsers(); }, [fetchLead, fetchActivity, fetchUsers]);

  const userMap = {};
  for (const u of users) userMap[u.id] = u;

  const resolveUserName = (userId) => {
    if (!userId) return null;
    if (userId === currentUser?.id) return 'You';
    return userMap[userId]?.name || null;
  };

  const canDelete = () => {
    if (currentUser?.role === 'admin') return true;
    return lead?.created_by === currentUser?.id;
  };

  const canEdit = () => {
    if (currentUser?.role === 'admin') return true;
    if (!lead) return false;
    return lead.created_by === currentUser?.id || lead.assigned_to === currentUser?.id;
  };

  const handleStageChange = async (stage) => {
    if (!lead || stage === lead.stage) return;
    try {
      const res = await leadsApi.update(id, { stage });
      setLead(res.data);
      fetchActivity();
    } catch {
      setError('Failed to update stage.');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await leadsApi.addNote(id, newNote.trim());
      setNewNote('');
      fetchActivity();
    } catch {
      setError('Failed to add note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleEdited = () => {
    setShowEdit(false);
    fetchLead();
    fetchActivity();
  };

  const handleDeleted = () => {
    setShowDelete(false);
    navigate('/leads');
  };

  const handleAssigned = async (agentId) => {
    try {
      const res = await leadsApi.assign(id, agentId);
      setLead(res.data);
      setShowAssign(false);
      fetchActivity();
    } catch {
      setError('Failed to assign lead.');
    }
  };

  if (loading) {
    return (
      <div className="lead-detail-page">
        <div className="ld-loading">
          <div className="ld-loading-spinner" />
          <p>Loading lead...</p>
        </div>
      </div>
    );
  }

  if (error && !lead) {
    return (
      <div className="lead-detail-page">
        <div className="breadcrumb">
          <Link to="/leads" className="breadcrumb-link">Leads</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Error</span>
        </div>
        <div className="ld-error-card card">
          <p>{error}</p>
          <Link to="/leads" className="btn btn-ghost" style={{ marginTop: 12 }}>Back to leads</Link>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const currentStageIdx = STAGES.indexOf(lead.stage);
  const assignedUser = userMap[lead.assigned_to];

  return (
    <div className="lead-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb animate-fade-up">
        <Link to="/leads" className="breadcrumb-link">Leads</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">{lead.name}</span>
      </div>

      {error && <div className="ld-error animate-fade-up">{error}</div>}

      {/* Header */}
      <div className="lead-detail-header animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="lead-detail-identity">
          <div className="lead-avatar-lg">
            {lead.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
          </div>
          <div>
            <h1 className="lead-detail-name">{lead.name}</h1>
            <div className="lead-detail-meta">
              <span className="source-badge">{lead.source}</span>
              {lead.address && <span className="lead-detail-address">{lead.address}</span>}
              <span className="lead-detail-added">Added {formatDate(lead.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="lead-detail-actions">
          <button className="btn btn-ghost">
            <PhoneIcon /> Call
          </button>
          <button className="btn btn-ghost">
            <MailIcon /> Email
          </button>
          {canEdit() && (
            <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
              <EditIcon /> Edit
            </button>
          )}
          {canDelete() && (
            <button className="btn btn-ghost btn-danger-text" onClick={() => setShowDelete(true)}>
              <TrashIcon /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Stage pipeline */}
      <div className="card lead-stage-card animate-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="stage-pipeline">
          {STAGES.map((stage, i) => {
            const isActive = lead.stage === stage;
            const isDone = i < currentStageIdx;
            const isLost = stage === 'Lost';
            return (
              <React.Fragment key={stage}>
                <button
                  className={`stage-step ${isActive ? 'stage-step--active' : ''} ${isDone ? 'stage-step--done' : ''} ${isLost ? 'stage-step--lost' : ''}`}
                  onClick={() => canEdit() && handleStageChange(stage)}
                  disabled={!canEdit()}
                >
                  <span className="stage-step-dot">
                    {isDone ? <CheckIcon /> : i + 1}
                  </span>
                  <span className="stage-step-label">{stage}</span>
                </button>
                {i < STAGES.length - 1 && (
                  <div className={`stage-connector ${i < currentStageIdx ? 'stage-connector--done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Body grid */}
      <div className="lead-detail-grid animate-fade-up" style={{ animationDelay: '120ms' }}>
        {/* Left: Details */}
        <div className="lead-detail-left">
          {/* Contact info */}
          <div className="card">
            <h3 className="detail-section-title">Contact Information</h3>
            <div className="detail-fields">
              <DetailField label="Full Name" value={lead.name} />
              <DetailField label="Phone" value={lead.phone} monospace />
              <DetailField label="Email" value={lead.email} monospace />
              <DetailField label="Address" value={lead.address} />
            </div>
          </div>

          {/* Property details */}
          <div className="card">
            <h3 className="detail-section-title">Property Details</h3>
            <div className="detail-fields">
              <DetailField label="Project Name" value={lead.project_name} />
              <DetailField label="Type" value={lead.type} />
              <DetailField label="Budget" value={lead.budget} accent />
              <DetailField label="Reference #" value={lead.reference_number} monospace />
              <DetailField label="Remark" value={lead.remark} />
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <h3 className="detail-section-title">Notes</h3>
            <p className="lead-notes-text">{lead.notes || 'No notes yet.'}</p>

            {canEdit() && (
              <div className="note-add-wrap">
                <textarea
                  className="input note-input"
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                />
                <div className="note-add-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!newNote.trim() || savingNote}
                    onClick={handleAddNote}
                  >
                    {savingNote ? 'Saving...' : 'Save note'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Sidebar */}
        <div className="lead-detail-right">
          {/* Assignment */}
          <div className="card card-sm">
            <p className="section-label" style={{ marginBottom: 12 }}>Assignment</p>
            {lead.assigned_to ? (
              <div className="assigned-agent">
                <div className="agent-avatar">
                  {(resolveUserName(lead.assigned_to) || 'A')[0]}
                </div>
                <div>
                  <div className="agent-name">{resolveUserName(lead.assigned_to) || 'Assigned'}</div>
                  <div className="agent-role">{assignedUser?.role === 'admin' ? 'Admin' : 'Sales Agent'}</div>
                </div>
                {currentUser?.role === 'admin' && (
                  <button className="btn-icon" title="Reassign" style={{ marginLeft: 'auto' }} onClick={() => setShowAssign(true)}>
                    <SwapIcon />
                  </button>
                )}
              </div>
            ) : (
              <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setShowAssign(true)}>
                <PlusIcon /> Assign agent
              </button>
            )}
          </div>

          {/* Key metrics */}
          <div className="card card-sm">
            <p className="section-label" style={{ marginBottom: 12 }}>Lead Details</p>
            <div className="lead-meta-list">
              <div className="lead-meta-item">
                <span className="lead-meta-label">Source</span>
                <span className="source-badge">{lead.source}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Status</span>
                <StageBadge stage={lead.stage} />
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Budget</span>
                <span className="lead-meta-value accent">{lead.budget || '—'}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Created</span>
                <span className="lead-meta-value">{formatDate(lead.created_at)}</span>
              </div>
              <div className="lead-meta-item">
                <span className="lead-meta-label">Last update</span>
                <span className="lead-meta-value">{timeAgo(lead.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Activity timeline */}
          <div className="card card-sm">
            <p className="section-label" style={{ marginBottom: 16 }}>Activity</p>
            {activity.length === 0 ? (
              <p className="ld-no-activity">No activity yet.</p>
            ) : (
              <div className="activity-timeline">
                {activity.map((act, i) => (
                  <div key={act.id} className="activity-item">
                    <div className="activity-icon-wrap">
                      <div className={`activity-icon activity-icon--${act.type}`}>
                        <ActivityTypeIcon type={act.type} />
                      </div>
                      {i < activity.length - 1 && <div className="activity-line" />}
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">{act.text}</p>
                      <div className="activity-meta">
                        <span className="activity-actor">{act.actor_name}</span>
                        <span className="activity-time">{timeAgo(act.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {showEdit && (
        <LeadEditModal
          lead={lead}
          users={users}
          currentUser={currentUser}
          onClose={() => setShowEdit(false)}
          onSaved={handleEdited}
        />
      )}

      {/* Delete confirmation */}
      {showDelete && (
        <DeleteLeadModal
          lead={lead}
          onClose={() => setShowDelete(false)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Assign modal */}
      {showAssign && (
        <AssignModal
          users={users}
          currentAssigned={lead.assigned_to}
          onClose={() => setShowAssign(false)}
          onAssign={handleAssigned}
        />
      )}
    </div>
  );
}


/* ─── Lead Edit Modal ───────────────────────────────────────────────────── */

function LeadEditModal({ lead, users, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: lead.name || '',
    phone: lead.phone || '',
    email: lead.email || '',
    source: lead.source || 'Manual',
    stage: lead.stage || 'New',
    budget: lead.budget || '',
    address: lead.address || '',
    remark: lead.remark || '',
    reference_number: lead.reference_number || '',
    type: lead.type || '',
    project_name: lead.project_name || '',
    notes: lead.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const updates = {};
      for (const [key, value] of Object.entries(form)) {
        if (value !== (lead[key] || '')) {
          updates[key] = value || null;
        }
      }
      // Ensure required fields stay set
      if ('name' in updates && !updates.name) delete updates.name;
      if ('phone' in updates && !updates.phone) delete updates.phone;

      if (Object.keys(updates).length > 0) {
        await leadsApi.update(lead.id, updates);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update lead.');
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Modal title="Edit Lead" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="ld-form-row">
            <div className="form-group">
              <label className="section-label">Name *</label>
              <input className="input" value={form.name} onChange={set('name')} required />
            </div>
            <div className="form-group">
              <label className="section-label">Phone *</label>
              <input className="input" value={form.phone} onChange={set('phone')} required />
            </div>
          </div>

          <div className="ld-form-row">
            <div className="form-group">
              <label className="section-label">Email</label>
              <input className="input" type="email" value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group">
              <label className="section-label">Budget</label>
              <input className="input" value={form.budget} onChange={set('budget')} />
            </div>
          </div>

          <div className="ld-form-row">
            <div className="form-group">
              <label className="section-label">Source</label>
              <select className="input ld-select" value={form.source} onChange={set('source')}>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="section-label">Stage</label>
              <select className="input ld-select" value={form.stage} onChange={set('stage')}>
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="ld-form-row">
            <div className="form-group">
              <label className="section-label">Address</label>
              <input className="input" value={form.address} onChange={set('address')} />
            </div>
            <div className="form-group">
              <label className="section-label">Project Name</label>
              <input className="input" value={form.project_name} onChange={set('project_name')} />
            </div>
          </div>

          <div className="ld-form-row">
            <div className="form-group">
              <label className="section-label">Type</label>
              <input className="input" value={form.type} onChange={set('type')} />
            </div>
            <div className="form-group">
              <label className="section-label">Reference Number</label>
              <input className="input" value={form.reference_number} onChange={set('reference_number')} />
            </div>
          </div>

          <div className="form-group">
            <label className="section-label">Remark</label>
            <textarea className="input ld-textarea" value={form.remark} onChange={set('remark')} rows={2} />
          </div>

          <div className="form-group">
            <label className="section-label">Notes</label>
            <textarea className="input ld-textarea" value={form.notes} onChange={set('notes')} rows={3} />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ─── Delete Confirmation Modal ─────────────────────────────────────────── */

function DeleteLeadModal({ lead, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await leadsApi.delete(lead.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete lead.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Delete Lead" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete <strong>{lead.name}</strong>?
        </p>
        <p className="delete-hint">
          This lead will be archived and no longer visible in the leads list.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Lead'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Assign Modal ──────────────────────────────────────────────────────── */

function AssignModal({ users, currentAssigned, onClose, onAssign }) {
  const activeUsers = users.filter(u => u.is_active);
  return (
    <Modal title="Assign Lead" size="sm" onClose={onClose}>
      <div className="modal-body">
        {activeUsers.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No team members available.</p>
        ) : (
          <div className="ld-assign-list">
            {activeUsers.map(u => (
              <button
                key={u.id}
                className={`ld-assign-item ${u.id === currentAssigned ? 'ld-assign-item--current' : ''}`}
                onClick={() => onAssign(u.id)}
                disabled={u.id === currentAssigned}
              >
                <div className="agent-avatar">{u.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                <div>
                  <div className="agent-name">{u.name}</div>
                  <div className="agent-role">{u.role === 'admin' ? 'Admin' : 'Agent'}</div>
                </div>
                {u.id === currentAssigned && <span className="ld-assign-current">Current</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}


/* ─── Sub-components ─────────────────────────────────────────────────────── */

function DetailField({ label, value, monospace, accent }) {
  return (
    <div className="detail-field">
      <span className="detail-field-label">{label}</span>
      <span className={`detail-field-value ${monospace ? 'monospace' : ''} ${accent ? 'accent' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}

function StageBadge({ stage }) {
  const map = { 'New': 'new', 'Contacted': 'contact', 'Site Visit': 'visit', 'Negotiation': 'negotiation', 'Closed': 'closed', 'Lost': 'lost' };
  return <span className={`badge badge-${map[stage] || 'default'}`}><span className="badge-dot" />{stage}</span>;
}

function ActivityTypeIcon({ type }) {
  if (type === 'note')   return <NoteIcon />;
  if (type === 'call')   return <PhoneIcon />;
  if (type === 'stage')  return <StageIcon />;
  if (type === 'assign') return <AssignIconSm />;
  if (type === 'delete') return <DeleteActivityIcon />;
  return <SparkIcon />;
}


/* ─── Page-specific icons ───────────────────────────────────────────────── */

function CheckIcon() { return <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function PhoneIcon() { return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3.5 2h3l1.5 3.5-2 1.5c.8 1.6 2 2.8 3.5 3.5l1.5-2L15 10v3c0 .5-.5 1-1 1C5 14 2 5 2 3.5c0-.5.5-1 1-1h.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>; }
function MailIcon() { return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M1.5 5l6.5 4 6.5-4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>; }
function SwapIcon() { return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 5h10M10 2l3 3-3 3M13 11H3M6 8l-3 3 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function NoteIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><rect x="2" y="1.5" width="10" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 5h4M5 7.5h4M5 10h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>; }
function StageIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 7l2 2 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function AssignIconSm() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><circle cx="5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.3"/><path d="M1 12c0-2.2 1.8-4 4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M9.5 8v4M7.5 10h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>; }
function SparkIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1l1.3 4.3H13l-3.65 2.65 1.4 4.3L7 9.5l-3.75 2.75 1.4-4.3L1 5.3h4.7L7 1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>; }
function DeleteActivityIcon() { return <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M3 4h8M5 4V3a1 1 0 011-1h2a1 1 0 011 1v1M4.5 4v7a1 1 0 001 1h3a1 1 0 001-1V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
